import { FC, useCallback, useMemo } from "react";
import { StudyTaskCard } from "@/features/study/components/StudyTaskCard";
import { RecommendationSkeleton } from "@/features/dashboard/components/skeletons/RecommendationSkeleton";
import { IconThumbUpAlt } from "@/assets/icons";
import { orange, yellow } from "@/theme/colors";
import { TRecommendation } from "@/infrastructure/adapters/study";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { showError } from "@/utils/error";
import { navigateToRecommendation } from "@/features/study/utils/navigateToRecommendation";
import { useStudySettings } from "@/infrastructure/adapters";
import { toast } from "@/utils/toast";

interface SuggestedTaskCardProps {
  recommendation: TRecommendation | null;
}

export const SuggestedTaskCard: FC<SuggestedTaskCardProps> = ({ recommendation }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { settings, updateSettings, isUpdating } = useStudySettings();

  const handleCardClick = useCallback(() => {
    if (!recommendation) return;
    navigateToRecommendation(navigate, recommendation);
  }, [navigate, recommendation]);

  const handleTurnOff = useCallback(async () => {
    try {
      await updateSettings({ isRecommendationEnabled: false });
      toast.success(t("common.saved"));
    } catch (error) {
      showError(error);
    }
  }, [updateSettings, t]);

  const card = useMemo(() => {
    if (!recommendation) return { title: "" };
    const displayText = recommendation.type === "MOCK_EXAM" ? recommendation.displayText : recommendation.subjectName;
    return { title: displayText };
  }, [recommendation]);

  if (isUpdating) {
    return <RecommendationSkeleton />;
  }

  if (!recommendation || !settings?.isRecommendationEnabled) return null;

  return (
    <StudyTaskCard
      title={t("dashboard.recommended")}
      accentColor={orange[80]}
      bgColor={yellow[50]}
      titleColor={yellow[900]}
      card={card}
      leftIcon={<IconThumbUpAlt color={orange[80]} />}
      onClick={handleCardClick}
      onClose={handleTurnOff}
    />
  );
};
